// Copy of packages/ui-web/src/envRibbon.ts — this app is a standalone npm
// project and cannot resolve the workspace package. Keep them in step.
// A strip naming the environment, on every page of every app except production.
//
// Dev and production run the same software behind near-identical domains
// (tip.dev.mana.services vs tip.mana.services), and the only visible difference
// is four characters in the address bar. That is how a test-mode payment comes
// to be read as a missing production payment. The strip sits in normal flow
// rather than floating over the page, so it can never cover a control, and
// production renders nothing — the absence of a marker is itself the signal.
//
// Plain DOM on purpose: the guest PWA, the staff Mini App and the venue console
// are three separate builds with different frameworks in play, and this has to
// look identical in all of them without dragging a UI runtime along.

/** Environments that are the real thing, and so wear no marker at all. */
const PRODUCTION = new Set(['production', 'prod', 'live']);

/** True for the environment that handles real money and real servers. */
export function isProductionEnv(env?: string | null): boolean {
  const name = (env ?? '').trim().toLowerCase();
  return name === '' || PRODUCTION.has(name);
}

/**
 * "DEV · " for anything that is not production, "" for production — meant for
 * the document title, often the only part of a browser tab still readable once
 * a dozen are open.
 */
export function envTitlePrefix(env?: string | null): string {
  return isProductionEnv(env) ? '' : `${(env ?? '').trim().toUpperCase()} · `;
}

/** Where the ribbon is remembered, so a second call cannot stack two of them. */
const RIBBON_ID = 'mana-env-ribbon';

/**
 * mountEnvRibbon inserts the strip at the top of the page and prefixes the
 * document title. Production (or an unset environment) does nothing at all.
 * Safe to call more than once.
 */
export function mountEnvRibbon(env?: string | null, note?: string): void {
  if (typeof document === 'undefined' || isProductionEnv(env)) return;
  if (document.getElementById(RIBBON_ID)) return;

  const name = (env ?? '').trim();
  const bar = document.createElement('div');
  bar.id = RIBBON_ID;
  bar.setAttribute('role', 'status');
  bar.style.cssText = [
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'gap:8px',
    'width:100%',
    'box-sizing:border-box',
    'padding:3px 12px',
    'background:#b45309',
    'color:#fff',
    'font:600 11px/1.4 ui-monospace,SFMono-Regular,Menlo,monospace',
    'letter-spacing:0.08em',
    'text-align:center',
  ].join(';');

  const label = document.createElement('span');
  label.textContent = name.toUpperCase();
  bar.appendChild(label);

  if (note) {
    const hint = document.createElement('span');
    hint.textContent = note;
    hint.style.cssText = 'font-weight:400;letter-spacing:normal;opacity:.9;' +
      'overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0';
    bar.appendChild(hint);
  }

  document.body.prepend(bar);
  document.title = envTitlePrefix(env) + document.title;
}
